import { useCallback, useMemo } from "react";
import {
  useNavigate as useReactRouterUseNavigate,
  useParams as useReactRouterUseParams,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import routeDescriptions from "../routers/routeDescriptions";
import { useStores } from "../models";

/**
 * 路由约定（react-router-dom@6.3）：
 * - 支持：/path/:id        必填 path 参数
 * - 不支持：/path/:id?      可选 path 参数（会匹配失败落到 * → /）
 * - 可选信息请用 query（?id=）或 location.state（对象）
 */
const useNavigation = () => {
  const { isMobile, setPanel } = useStores();
  const n = useReactRouterUseNavigate();
  const pathParams = useReactRouterUseParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const params = useMemo(() => {
    const fromQuery: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      fromQuery[key] = value;
    });
    const fromState =
      location.state && typeof location.state === "object"
        ? (location.state as Record<string, any>)
        : {};
    return { ...fromQuery, ...pathParams, ...fromState };
  }, [pathParams, searchParams, location.state]);

  const isPCLeftPanelRoutesKeys = useMemo(
    () =>
      routeDescriptions
        .filter((route) => route.isLeftPanel)
        .map(({ key }) => key),
    []
  );

  const navigate = useCallback(
    (pageKey, p?, navigateConfig?) => {
      p = p ? p : {};
      const matchRoute = routeDescriptions.find(
        (route) => route.key === pageKey
      );
      if (!matchRoute) {
        console.error("not match any route with key:", pageKey);
        return;
      }

      let path = matchRoute.path;
      const query: Record<string, string> = {};
      const state: Record<string, any> = {
        ...(navigateConfig?.state || {}),
      };

      Object.keys(p).forEach((k) => {
        const token = `:${k}`;
        const value = p[k];
        if (path.includes(token)) {
          // 兼容历史 :id? 写法（若仍残留）
          const reg = new RegExp(`:${k}\\??`, "ig");
          path = path.replace(reg, encodeURIComponent(String(value)));
        } else if (value != null && value !== "") {
          if (typeof value === "object") {
            state[k] = value;
          } else {
            query[k] = String(value);
          }
        }
      });

      // 去掉未替换的可选段 /:foo?
      path = path.replace(/\/:[^/]+\?/g, "");

      // 仍残留必填 :param → 禁止跳转，避免被 * 重定向到 /
      const unresolved = path.match(/\/:[^/?]+/g);
      if (unresolved) {
        console.error(
          `[navigate] route "${pageKey}" missing path params ${unresolved.join(
            ", "
          )}, path would be "${path}"`
        );
        return;
      }

      const qs = Object.keys(query)
        .map(
          (k) => `${encodeURIComponent(k)}=${encodeURIComponent(query[k])}`
        )
        .join("&");
      if (qs) path = `${path}?${qs}`;

      const nextConfig = {
        ...navigateConfig,
        ...(Object.keys(state).length ? { state } : {}),
      };

      if (!isMobile && isPCLeftPanelRoutesKeys.includes(pageKey)) {
        setPanel(pageKey);
      } else {
        n(path, nextConfig);
      }
    },
    [isMobile, setPanel, isPCLeftPanelRoutesKeys, n]
  );

  const goBack = useCallback(
    (num) => {
      n(num);
    },
    [n]
  );

  return {
    n,
    navigate,
    params,
    goBack,
  };
};

export default useNavigation;
